
import React, { useRef, useState } from "react";

import { createAsset, deleteAsset, readApiError, renameAsset } from "./printSignApi";
import { AssetThumb, Choice, MAX_IMAGE_BYTES, Section, formatBytes, formatDateTime } from "./signUi";

const KIND_LABELS = { background: "Фон", photo: "Фото", icon: "Иконка" };

/**
 * Библиотека картинок: фоны вывесок, фото для блока преимуществ (рендер памятников),
 * свои иконки. Загружается один раз — выбирается в любой вывеске и профиле.
 */
const AssetLibrary = ({ assets, onChanged }) => {
    const [filter, setFilter] = useState("");
    const [kind, setKind] = useState("photo");
    const [busy, setBusy] = useState("");
    const [message, setMessage] = useState(null);
    const inputRef = useRef(null);

    const upload = async (files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setBusy("upload");
        setMessage(null);
        const created = [];
        try {
            for (const file of list) {
                if (file.size > MAX_IMAGE_BYTES) {
                    setMessage({ type: "error", text: `«${file.name}» больше 30 МБ — пропущен.` });
                    continue;
                }
                created.push(await createAsset({ kind, name: file.name.replace(/\.[^.]+$/, ""), file }));
            }
            if (created.length) {
                onChanged((current) => [...created, ...current]);
                setMessage({ type: "success", text: `Загружено: ${created.length}.` });
            }
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить") });
            if (created.length) onChanged((current) => [...created, ...current]);
        } finally {
            setBusy("");
        }
    };

    const rename = async (asset) => {
        const name = window.prompt("Название картинки", asset.name);
        if (name === null || !name.trim() || name.trim() === asset.name) return;
        try {
            const next = await renameAsset(asset._id, name.trim());
            onChanged((current) => current.map((item) => (item._id === next._id ? next : item)));
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось переименовать") });
        }
    };

    const remove = async (asset) => {
        if (!window.confirm(`Удалить «${asset.name}» из библиотеки?`)) return;
        setBusy(asset._id);
        try {
            await deleteAsset(asset._id);
            onChanged((current) => current.filter((item) => item._id !== asset._id));
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось удалить") });
        } finally {
            setBusy("");
        }
    };

    const visible = filter ? assets.filter((asset) => asset.kind === filter) : assets;

    return (
        <div className="printSigns__library">
            <Section title="Загрузить в библиотеку"
                hint="Фото и фоны хранятся в высоком качестве (до 8000 точек) — рендер на сотни памятников останется чётким и на большой вывеске. Иконки — PNG с прозрачным фоном.">
                <Choice
                    options={[
                        { value: "photo", label: "Фото", hint: "блок преимуществ" },
                        { value: "background", label: "Фон", hint: "весь лист" },
                        { value: "icon", label: "Иконка", hint: "PNG" },
                    ]}
                    value={kind}
                    disabled={!!busy}
                    onChange={setKind}
                />
                <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden
                    onChange={(event) => {
                        const { files } = event.target;
                        upload(files).finally(() => {
                            event.target.value = "";
                        });
                    }} />
                <div className="printCatalogs__buttons printSigns__uploadButtons">
                    <button type="button" className="is-primary" disabled={!!busy} onClick={() => inputRef.current?.click()}>
                        {busy === "upload" ? "Загрузка…" : "Выбрать файлы"}
                    </button>
                </div>
                {message ? <div className={`printCatalogs__message is-${message.type}`}>{message.text}</div> : null}
            </Section>

            <Section title={`Картинки: ${visible.length}`}>
                <Choice
                    options={[
                        { value: "", label: "Все" },
                        { value: "photo", label: "Фото" },
                        { value: "background", label: "Фоны" },
                        { value: "icon", label: "Иконки" },
                    ]}
                    value={filter}
                    onChange={setFilter}
                />
                {visible.length ? (
                    <div className="printSigns__libraryGrid">
                        {visible.map((asset) => (
                            <div key={asset._id} className="printSigns__libraryCard">
                                <AssetThumb assetId={asset._id} className={asset.kind === "icon" ? "is-icon" : ""} />
                                <strong title={asset.name}>{asset.name}</strong>
                                <span>
                                    {KIND_LABELS[asset.kind]} · {asset.width}×{asset.height} · {formatBytes(asset.sizeBytes)}
                                </span>
                                <span>{asset.createdByName || "—"} · {formatDateTime(asset.createdAt)}</span>
                                <div>
                                    <button type="button" onClick={() => rename(asset)}>Переименовать</button>
                                    <button type="button" className="is-danger" disabled={busy === asset._id} onClick={() => remove(asset)}>Удалить</button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="printCatalogs__hint">Здесь пока пусто.</p>
                )}
            </Section>
        </div>
    );
};

export default AssetLibrary;
